// ink-component
import React from 'react';
import camelCase from 'lodash/camelCase';
import styled from 'styled-components';

import { vignetteSizes } from './styles';
import { VignetteTypes } from '../../helpers/constants/vignettes';
import getGlobalProps from '../../helpers/constants/global-props';

import * as vignetteIcons from './components';

type VignetteSizeType = 'sm' | 'md' | 'lg';
type VignetteType = VignetteTypes | string;

export interface VignetteProps {
  /** String identifier for the vignette to be displayed. */
  type?: VignetteType;
  /** Simple description for screen readers */
  meaning?: string;
  /** Size modifier */
  size?: VignetteSizeType;
  /** Hide element from the accessibility tree when set to "true" */
  'aria-hidden'?: 'true' | 'false';
}

const globalProps = getGlobalProps({});

const SVGIcon = ({ type, size, ...otherProps }: { type?: VignetteType; size?: VignetteSizeType }) => {
  const isAnimated = type === 'documentanimated' || type === 'notfoundanimated';
  const adjustedSize = isAnimated ? 'lg' : size || 'md';
  const Component = vignetteIcons[camelCase(`${type}-${adjustedSize}`)]
    ? vignetteIcons[camelCase(`${type}-${adjustedSize}`)]
    : vignetteIcons.documentMd;

  return <Component {...otherProps} />;
};

const StyledVignette = styled(SVGIcon)<{ size?: VignetteSizeType; type?: VignetteType }>`
  ${({ size }) => vignetteSizes[size || 'md']};
`;

/**
 * @purpose Aids user interface to convey a message with contextual illustration
 * @categories accessory
 * @aka illustration
 */
function Vignette(props: VignetteProps) {
  const { type, meaning } = props;
  const title = meaning || type;

  const getAriaLabel = () => {
    if (props['aria-hidden'] === 'true') return undefined;

    if (props['aria-label']) return props['aria-label'];

    if (meaning) return meaning;

    return `${title} icon`;
  };

  const svgProps = {
    role: 'img',
    xmlns: 'http://www.w3.org/2000/svg',
    viewBox: '0 0 512 512',
    width: '1em',
    height: '1em',
    ...globalProps,
    ...props,
    'aria-hidden': props['aria-hidden'] === 'true' ? 'true' : undefined,
    'aria-label': getAriaLabel(),
  };

  return <StyledVignette {...svgProps}>{title !== type && <title>{title}</title>}</StyledVignette>;
}

Vignette.defaultProps = {
  type: 'document',
  meaning: undefined,
  size: 'md',
  'aria-hidden': undefined,
};

Vignette.displayName = 'Vignette';

export default Vignette;
